import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

import HomePage from './pages/HomePage';
// import CollectionPage from './pages/CollectionPage'; // REMOVED - Now using gammes section on homepage
import Checkout from './pages/Checkout';

const TrackOrderPage = React.lazy(() => import('./pages/TrackOrderPage'));
const ReturnPolicyPage = React.lazy(() => import('./pages/ReturnPolicyPage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage')); // Lazy loaded as requested

const Custom = React.lazy(() => import('./pages/Custom'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Signup = React.lazy(() => import('./pages/Signup'));
const Login = React.lazy(() => import('./pages/Login'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminProducts = React.lazy(() => import('./pages/admin/AdminProducts'));
const AdminMedia = React.lazy(() => import('./pages/admin/AdminMedia'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminLive = React.lazy(() => import('./pages/admin/AdminLive'));
const CGV = React.lazy(() => import('./pages/CGV'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const LegalNotice = React.lazy(() => import('./pages/LegalNotice'));
const Blog = React.lazy(() => import('./pages/Blog'));
const About = React.lazy(() => import('./pages/About'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Guide = React.lazy(() => import('./pages/Guide'));
const Team = React.lazy(() => import('./pages/Team'));
const OrderSuccess = React.lazy(() => import('./pages/OrderSuccess'));

const AIDesignStudio = React.lazy(() => import('./components/ai/AIDesignStudio'));
const Vinted = React.lazy(() => import('./pages/Vinted'));
const AIKnowledge = React.lazy(() => import('./pages/AIKnowledge'));
const AuthorityReport = React.lazy(() => import('./pages/AuthorityReport'));
const PochetteColis = React.lazy(() => import('./pages/PochetteColis'));
const EmballageColis = React.lazy(() => import('./pages/EmballageColis'));
const EnveloppePlastique = React.lazy(() => import('./pages/EnveloppePlastique'));
const SacExpedition = React.lazy(() => import('./pages/SacExpedition'));
const PochetteVinted = React.lazy(() => import('./pages/PochetteVinted'));
const EmballageVinted = React.lazy(() => import('./pages/EmballageVinted'));
const PochetteExpedition = React.lazy(() => import('./pages/PochetteExpedition'));
const EmballagePlastique = React.lazy(() => import('./pages/EmballagePlastique'));
const SacEnvoiColis = React.lazy(() => import('./pages/SacEnvoiColis'));
const ColisVinted = React.lazy(() => import('./pages/ColisVinted'));
const PochetteEnvoiColis = React.lazy(() => import('./pages/PochetteEnvoiColis'));
const EnveloppeColis = React.lazy(() => import('./pages/EnveloppeColis'));
const SacColis = React.lazy(() => import('./pages/SacColis'));
const SachetVinted = React.lazy(() => import('./pages/SachetVinted'));
const SacEmballageColis = React.lazy(() => import('./pages/SacEmballageColis'));
const SacVinted = React.lazy(() => import('./pages/SacVinted'));

import CustomCursor from './components/ui/CustomCursor';
import { soundEffects } from './components/audio/SoundEffects';
import { CookieConsentProvider } from './context/CookieConsentContext';
import CookieBanner from './components/CookieBanner';

import Navbar from './components/layout/Navbar';
import Footer from './components/Footer';
import LivingBackground from './components/layout/LivingBackground';
import PasswordGate from './components/PasswordGate';
import { supabase } from './supabaseClient';
import { initTracking, trackEvent } from './utils/Tracking';
import { Analytics } from '@vercel/analytics/react';
import { storeConfig } from './data/config';

function App() {
  const [theme, setTheme] = useState('tiktok');

  return (
    <CookieConsentProvider>
      <AppContent theme={theme} setTheme={setTheme} />
      <CookieBanner />
    </CookieConsentProvider>
  );
}

function AppContent({ theme, setTheme }) {


  const navigate = useNavigate();
  const location = useLocation();

  // Visitor Tracking
  useEffect(() => {
    const trackVisit = async () => {
      if (import.meta.env.MODE === 'development') return;

      // Do not track Admin pages
      if (location.pathname.startsWith('/portal-secure')) return;

      try {
        // 1. Session ID (Unique Visitor)
        let sessionId = localStorage.getItem('visitor_session_id');
        if (!sessionId) {
          sessionId = crypto.randomUUID();
          localStorage.setItem('visitor_session_id', sessionId);
        }

        // 2. Geolocation (Cached to avoid API limits)
        let geo = JSON.parse(sessionStorage.getItem('visitor_geo') || 'null');
        if (!geo) {
          try {
            const res = await fetch('https://ipapi.co/json/');
            if (res.ok) {
              geo = await res.json();
              sessionStorage.setItem('visitor_geo', JSON.stringify(geo));
            }
          } catch (e) {
            console.warn('Geo fetch error:', e);
          }
        }

        // 3. Insert Record
        await supabase.from('visits').insert([{
          page: location.pathname,
          ref: document.referrer,
          session_id: sessionId,
          city: geo?.city || 'Unknown',
          country: geo?.country_name || 'Unknown',
          lat: geo?.latitude || 0,
          lng: geo?.longitude || 0
        }]);

      } catch (err) {
        // Silent fail
      }
    };
    trackVisit();
  }, [location.pathname]);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    /* 
    // Lenis disabled to fix scroll glitches
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => lenis.destroy();
    */
  }, []);

  const changeTheme = (newTheme, hexColor = null) => {
    setTheme(newTheme);
    const body = document.body;
    body.className = '';

    if (newTheme === 'tiktok') {
      applyGlobalColor('#00F0FF');
    } else if (newTheme === 'vinted') {
      body.classList.add('theme-vinted');
      applyGlobalColor('#09B1BA');
    } else if (newTheme === 'unicolor') {
      body.classList.add('theme-unicolor');
      applyGlobalColor(hexColor || '#D4AF37');
    }
  };

  const applyGlobalColor = (hex) => {
    const root = document.documentElement.style;
    root.setProperty('--primary', hex);
    root.setProperty('--primary-dim', hex + '26');
  };

  useEffect(() => {
    if (location.pathname === '/preview') {
      changeTheme('tiktok');
    }
    window.scrollTo(0, 0);

    // Dynamic Canonical Tag Management - REMOVED (Handled by Helmet now)
    // to prevent duplicate tags and SEO penalties.
  }, [location.pathname]);



  const navTo = (path) => {
    soundEffects.play('click');
    navigate(path);
  };

  return (
    <PasswordGate>
      <div
        data-store-key={storeConfig.storeKey}
        className={`app-container ${theme}-theme min-h-screen bg-[var(--bg-color)] text-[var(--text-color)] transition-colors duration-500 relative`}
      >
        <Analytics />
        <CustomCursor />
        <LivingBackground theme={theme} />

        {/* Hide Navbar on product pages with custom layout */}
        <Navbar theme={theme} />

        <main className="relative z-10">
          <React.Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-black">
              <div className="w-12 h-12 border-4 border-[#00f2ea] border-t-transparent rounded-full animate-spin"></div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<HomePage theme={theme} changeTheme={changeTheme} />} />

              <Route path="/shop" element={<Navigate to="/#buybox" replace />} />
              <Route path="/collection" element={<Navigate to="/#buybox" replace />} />
              <Route path="/track" element={<TrackOrderPage />} />
              <Route path="/politique-de-retour" element={<ReturnPolicyPage />} />
              <Route path="/checkout" element={<Checkout />} />

              <Route path="/custom" element={<Custom />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/order-confirmation" element={<OrderSuccess />} />
              <Route path="/cgv" element={<CGV />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<LegalNotice />} />
              <Route path="/design-studio" element={<AIDesignStudio />} />
              <Route path="/ai-studio" element={<AIDesignStudio />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Blog />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/team" element={<Team />} />
              <Route path="/ai-knowledge" element={<AIKnowledge />} />
              <Route path="/vinted" element={<Vinted />} />
              <Route path="/guide" element={<Guide />} />

              {/* 🚀 SEO REDIRECTS - SATELLITE PAGES CONSOLIDATION */}
              {/* All satellite pages redirect to main pillar pages to fix keyword cannibalization */}
              <Route path="/pochette-expedition-vinted" element={<Navigate to="/vinted" replace />} />
              <Route path="/sac-expedition-vinted" element={<Navigate to="/vinted" replace />} />
              <Route path="/pochette-colis" element={<Navigate to="/" replace />} />
              <Route path="/emballage-colis" element={<Navigate to="/" replace />} />
              <Route path="/enveloppe-plastique" element={<Navigate to="/" replace />} />
              <Route path="/sac-expedition" element={<Navigate to="/" replace />} />
              <Route path="/pochette-vinted" element={<Navigate to="/vinted" replace />} />
              <Route path="/emballage-vinted" element={<Navigate to="/vinted" replace />} />
              <Route path="/pochette-expedition" element={<Navigate to="/" replace />} />
              <Route path="/emballage-plastique" element={<Navigate to="/" replace />} />
              <Route path="/sac-envoi-colis" element={<Navigate to="/" replace />} />
              <Route path="/colis-vinted" element={<Navigate to="/vinted" replace />} />
              <Route path="/pochette-envoi-colis" element={<Navigate to="/" replace />} />
              <Route path="/enveloppe-colis" element={<Navigate to="/" replace />} />
              <Route path="/sac-colis" element={<Navigate to="/" replace />} />
              <Route path="/sachet-vinted" element={<Navigate to="/vinted" replace />} />
              <Route path="/sac-emballage-colis" element={<Navigate to="/" replace />} />
              <Route path="/sac-vinted" element={<Navigate to="/vinted" replace />} />

              <Route path="/expertise-logistique-2025" element={<AuthorityReport />} />
              <Route path="/produit/:slug" element={<ProductPage key={location.pathname} />} />

              {/* Admin Routes */}
              <Route path="/portal-secure/login" element={<AdminLogin />} />
              <Route path="/portal-secure" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="live" element={<AdminLive />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="media" element={<AdminMedia />} />
              </Route>
            </Routes>
          </React.Suspense>
        </main>

        {/* Hide Footer on product pages with custom layout */}
        <Footer theme={theme} />


      </div>
    </PasswordGate >
  );
}

export default App;
